import { useState } from 'react'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { updateTenant } from '../../api/tenant'
import api from '../../lib/api'

const inputStyle = { width: '100%', padding: '8px 12px', border: '0.5px solid #e0e3f0', borderRadius: 8, fontSize: 13, background: 'white', color: '#111', outline: 'none', boxSizing: 'border-box' }
const readOnlyFieldStyle = { ...inputStyle, background: '#f5f6f8', color: '#374151', cursor: 'default' }
const cardStyle  = { background: 'white', border: '0.5px solid #e0e3f0', borderRadius: 12, padding: 18, marginBottom: 12 }
const labelStyle = { fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block' }

export default function ChatSettings({ tenantId, bot, onUpdate }) {
  const { t } = useTranslation()
  const [form, setForm] = useState({
    name:           bot?.name           ?? '',
    bot_persona:    bot?.bot_persona    ?? '',
    opening_hours:  bot?.opening_hours  ?? '',
    address:        bot?.address        ?? '',
    language:       bot?.language       ?? 'en',
    persona_gender: bot?.persona_gender ?? 'female',
    self_ref:       bot?.self_ref       ?? '',
    tone:           bot?.tone           ?? 'professional',
    max_length:     bot?.max_length     ?? 300,
  })
  const [saved, setSaved]   = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const needsViFields = form.language === 'vi' || form.language === 'bilingual'

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); setSaved(false); setSaveError('') }

  async function handleSave() {
    setSaving(true)
    setSaveError('')
    try {
      await updateTenant(tenantId, form)
      setSaved(true)
      api.post(`/admin/kb/retrain/${tenantId}?bot_type=chatizi`).catch(() => {})
      setTimeout(() => setSaved(false), 2000)
      onUpdate()
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: 'save_chat_settings' },
        extra: { tenantId },
      })
      setSaveError(err?.response?.data?.message || err?.message || 'Error')
    }
    setSaving(false)
  }

  return (
    <div>
      <div style={cardStyle}>
        <label style={labelStyle}>{t('chatSettings.businessName')}</label>
        <input style={inputStyle} value={form.name} onChange={e => set('name', e.target.value)} />
      </div>

      <div style={cardStyle}>
        <label style={labelStyle}>{t('chatSettings.botPersona')}</label>
        <textarea rows={3} style={{ ...inputStyle, resize: 'vertical', minHeight: 80, fontFamily: 'inherit' }} value={form.bot_persona} onChange={e => set('bot_persona', e.target.value)} />
      </div>

      <div style={cardStyle}>
        <label style={labelStyle}>{t('chatSettings.openingHours')}</label>
        <input style={inputStyle} value={form.opening_hours} onChange={e => set('opening_hours', e.target.value)} placeholder={t('chatSettings.openingHoursPh')} />
      </div>

      <div style={cardStyle}>
        <label style={labelStyle}>{t('chatSettings.address')}</label>
        <input style={inputStyle} value={form.address} onChange={e => set('address', e.target.value)} />
      </div>

      <div style={cardStyle}>
        <label style={labelStyle}>{t('chatSettings.language')}</label>
        <select style={inputStyle} value={form.language} onChange={e => set('language', e.target.value)}>
          <option value="en">{t('language.en')}</option>
          <option value="vi">{t('language.vi')}</option>
          <option value="bilingual">{t('language.bilingual')}</option>
        </select>
      </div>

      {needsViFields && (
        <>
          <div style={cardStyle}>
            <label style={labelStyle}>{t('chatSettings.personaGender')}</label>
            <select style={inputStyle} value={form.persona_gender} onChange={e => set('persona_gender', e.target.value)}>
              <option value="female">{t('chatSettings.female')}</option>
              <option value="male">{t('chatSettings.male')}</option>
            </select>
          </div>
          <div style={cardStyle}>
            <label style={labelStyle}>{t('chatSettings.selfRef')}</label>
            <input style={inputStyle} value={form.self_ref} onChange={e => set('self_ref', e.target.value)} placeholder={t('chatSettings.selfRefPh')} />
          </div>
        </>
      )}

      <div style={cardStyle}>
        <label style={labelStyle}>{t('chatSettings.tone')}</label>
        <select style={inputStyle} value={form.tone} onChange={e => set('tone', e.target.value)}>
          <option value="professional">{t('chatSettings.toneProfessional')}</option>
          <option value="friendly">{t('chatSettings.toneFriendly')}</option>
          <option value="casual">{t('chatSettings.toneCasual')}</option>
        </select>
      </div>

      <div style={cardStyle}>
        <label style={labelStyle}>{t('chatSettings.maxLengthLabel')}</label>
        <div style={readOnlyFieldStyle}>{t('chatSettings.maxLengthTokens', { n: form.max_length })}</div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={handleSave} disabled={saving}
          style={{ background: saved ? '#16a34a' : '#1535c8', color: 'white', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? t('common.saving') : saved ? t('common.saved') : t('common.saveChanges')}
        </button>
        {saveError && <span style={{ color: '#ef4444', fontSize: 12 }}>{saveError}</span>}
      </div>
    </div>
  )
}
